import React, { useEffect, useMemo } from "react";
import Avatar2D from "@/components/Avatar2D";
import { crownSvg } from "@/lib/crownSvg";

// Raider Races podium (Jack, 2026-10-25): when a race ends the blocks rise,
// then the raiders jump on from last place to the winner, who gets a crown
// and confetti. The result pop-up waits for onDone (after the show, or a tap).
// `racers`: [{ name, character, rows, won }], rows = rows still to go to the
// finish line (0 = crossed it). Two or three racers.

const SHOW_MS = 5200;

const CSS = `
.rpod{position:absolute;inset:0;z-index:60;display:flex;flex-direction:column;justify-content:flex-end;padding-bottom:12vh;overflow:hidden;cursor:pointer;
  background:radial-gradient(ellipse at 50% 18%,rgba(42,53,80,.97),rgba(20,24,34,.97) 70%);animation:rpod-in .35s ease-out}
.rpod-banner{position:absolute;top:12%;left:0;right:0;padding:0 16px;text-align:center;color:#fff;font-weight:800;font-size:24px;opacity:0;transform:scale(.6);animation:rpod-pop .5s forwards}
.rpod-pods{display:flex;align-items:flex-end;justify-content:center;gap:6px;padding:0 8px}
.rpod-pod{width:118px;display:flex;flex-direction:column;align-items:center}
.rpod-block{width:100%;border-radius:8px 8px 0 0;display:flex;justify-content:center;padding-top:8px;font-weight:900;font-size:30px;color:#1b1f2a;transform:scaleY(0);transform-origin:bottom;animation:rpod-grow .45s ease-out forwards}
.rpod-1 .rpod-block{height:150px;background:linear-gradient(#ffe08a,#f2c94c)}
.rpod-2 .rpod-block{height:105px;background:linear-gradient(#eef2f7,#c9d1dc)}
.rpod-3 .rpod-block{height:70px;background:linear-gradient(#ecb07c,#d08a4f)}
.rpod-rd{width:104px;height:111px;opacity:0;transform:translateY(-260px);animation:rpod-drop .55s cubic-bezier(.3,1.5,.6,1) forwards}
.rpod-rd svg{width:100%;height:100%}
.rpod-1 .rpod-rd svg{filter:drop-shadow(0 0 14px rgba(242,201,76,.55))}
.rpod-name{color:#fff;font-weight:700;font-size:13px;margin-top:2px;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;opacity:0;animation:rpod-fade .4s forwards}
.rpod-sub{color:#9aa3b5;font-size:11px;opacity:0;animation:rpod-fade .4s forwards}
.rpod-crown{width:58px;height:30px;display:flex;align-items:flex-end;opacity:0;transform:scale(.6);animation:rpod-pop .4s forwards;filter:drop-shadow(0 1px 1px rgba(0,0,0,.45)) drop-shadow(0 0 4px rgba(251,191,36,.6))}
.rpod-conf{position:absolute;top:-12px;width:8px;height:12px;border-radius:2px;opacity:0;animation:rpod-fall 2.8s linear forwards}
.rpod-skip{position:absolute;bottom:8px;right:10px;color:#9aa3b5;font-size:11px}
@keyframes rpod-in{from{opacity:0}}
@keyframes rpod-grow{to{transform:scaleY(1)}}
@keyframes rpod-drop{to{opacity:1;transform:translateY(0)}}
@keyframes rpod-fade{to{opacity:1}}
@keyframes rpod-pop{to{opacity:1;transform:scale(1)}}
@keyframes rpod-fall{0%{opacity:1;transform:translateY(0) rotate(0)}100%{opacity:1;transform:translateY(110vh) rotate(720deg)}}
@media (max-width:420px){.rpod-pod{width:96px}.rpod-rd{width:84px;height:90px}}
`;

const PODIUM_CROWN = crownSvg("podium");
const CONFETTI = ["#f2c94c", "#98bf86", "#3d8bfd", "#e05a5a", "#c77dff"];

// Places: the winner first, then whoever got nearest the finish.
export function racePlaces(racers) {
  return [...racers].sort((a, b) => (b.won ? 1 : 0) - (a.won ? 1 : 0) || (a.rows ?? 99) - (b.rows ?? 99));
}

function subText(r) {
  if (r.won && !r.rows) return "Crossed the line";
  if (r.rows == null) return "";
  return `${r.rows} row${r.rows === 1 ? "" : "s"} to go`;
}

export default function RacePodium({ racers, onDone }) {
  const places = useMemo(() => racePlaces(racers || []), [racers]);
  useEffect(() => {
    const t = setTimeout(() => onDone?.(), SHOW_MS);
    return () => clearTimeout(t);
  }, [onDone]);
  const confetti = useMemo(() => Array.from({ length: 40 }, (_, i) => ({
    left: `${Math.random() * 100}%`, background: CONFETTI[i % 5], animationDelay: `${2.3 + Math.random() * 1.2}s`,
  })), []);
  if (!places.length) return null;
  const n = places.length;
  // Jump on from last place to first; the winner lands last.
  const dropAt = (place) => 0.8 + (n - place) * 0.5;
  // Left to right: 2nd, 1st, 3rd.
  const order = n >= 3 ? [1, 0, 2] : [1, 0];
  return (
    <div className="rpod" onClick={() => onDone?.()} data-testid="race-podium">
      <style>{CSS}</style>
      <div className="rpod-banner" style={{ animationDelay: `${dropAt(1) + 0.5}s` }}>
        🏁 {places[0].you ? "You win" : `${places[0].name} wins`} the race!
      </div>
      {confetti.map((s, i) => <i key={i} className="rpod-conf" style={s} />)}
      <div className="rpod-pods">
        {order.filter((i) => places[i]).map((i) => {
          const r = places[i];
          const place = i + 1;
          const at = dropAt(place);
          return (
            <div key={i} className={`rpod-pod rpod-${place}`} data-testid={`race-podium-${place}`}>
              {/* The same gold crown as the leaderboard leader's (Jack, 2026-10-29). */}
              {place === 1 && <div className="rpod-crown" style={{ animationDelay: `${at + 0.5}s` }} dangerouslySetInnerHTML={{ __html: PODIUM_CROWN }} />}
              <div className="rpod-rd" style={{ animationDelay: `${at}s` }}>
                <Avatar2D character={r.character} pose={place === 1 ? "cheer" : place === 2 ? "wave" : "idle"} className="w-full h-full" />
              </div>
              <div className="rpod-name" style={{ animationDelay: `${at + 0.4}s` }}>{r.name}</div>
              <div className="rpod-sub" style={{ animationDelay: `${at + 0.4}s` }}>{subText(r)}</div>
              <div className="rpod-block" style={{ animationDelay: `${(n - place) * 0.15}s` }}>{place}</div>
            </div>
          );
        })}
      </div>
      <div className="rpod-skip">Tap to continue</div>
    </div>
  );
}
